import { CustomAxiosRequestConfig } from "@/interfaces/customAxiosRequestConfig";
import { Phone } from "@/interfaces/phone";
import axios from "@/lib/axios";
import { useUsersPhonesStore } from "@/stores/usersPhones";
import { useQuery } from "@tanstack/vue-query";
import { storeToRefs } from "pinia";
import { watch } from "vue";

interface Params {
  userId: number | string;
}

interface Props {
  immediate?: boolean;
  params: Params;
}

const getUserPhones = async (param: Params): Promise<Phone[]> => {
  const { data } = await axios.get<Phone[]>(
    `/api/users/${param.userId}/phones`,
    { errorHandling: false } as CustomAxiosRequestConfig,
  );

  return data;
};

const useUserPhones = (props: Props) => {
  const { params, immediate = true } = props || {};
  const { setUsersPhones } = useUsersPhonesStore();
  const { usersPhones } = storeToRefs(useUsersPhonesStore());

  const { data, isLoading, isError, refetch } = useQuery(
    ["userPhones", params.userId],
    () =>
      getUserPhones({
        userId: params.userId,
      }),
    {
      enabled: immediate,
      refetchOnWindowFocus: true,
      retry: false,
    },
  );

  watch(
    data,
    (value) => {
      if (value) {
        setUsersPhones(value);
      }
    },
    {
      immediate: immediate,
    },
  );

  return {
    data,
    usersPhones,
    isLoading,
    isError,
    refetch,
  };
};

export default useUserPhones;
